import { ActionButton, Checkbox, DefaultButton, Dropdown, Label, Modal, PrimaryButton, Stack, Toggle } from '@fluentui/react';
import { FC, useCallback } from 'react';
import InlineControl from '../components/shared/InlineControl';
import { ModalHeader, Separator } from '../shared/styled-components';
import { AgentState, AgentStates, IAgentReport } from '../models/reports.internal';
import { objectKeys } from '../utils/objectUtils';
import { nameof } from '../utils/stringUtils';
import getTotalWorkItemCounts from '../calculations/getTotalWorkItemCounts';
import { SortOptions } from '../components/shared/ReportList';

export interface MemberFilterOptions {
  hasCompletedTasks: boolean;
  states: Record<AgentState, boolean>;
}

export const defaultMemberFilters: MemberFilterOptions = {
  hasCompletedTasks: false,
  states: { Idle: true, Break: true, Busy: true, LoggedOut: true },
};

export const memberSortOptions: SortOptions<IAgentReport> = {
  'Name': 'name',
  'Tasks Handled': { selector: (ar) => getTotalWorkItemCounts(ar.workItemsByMedia).handled },
  'Tasks Missed': { selector: (ar) => getTotalWorkItemCounts(ar.workItemsByMedia).missed },
  'Voice Handled': { selector: (ar) => ar.workItemsByMedia.Voice.handled },
  'Email Handled': { selector: (ar) => ar.workItemsByMedia.Email.handled },
  'Messaging Handled': { selector: (ar) => ar.workItemsByMedia.Messaging.handled },
  'Webchat Handled': { selector: (ar) => ar.workItemsByMedia.Webchat.handled },
};

const sortDropdownOptions = objectKeys(memberSortOptions).map(key => ({ key, text: key }));

function getStateName(state: AgentState): string {
  switch (state) {
    case 'Busy':
      return 'Busy';
    case 'Break':
      return 'Break';
    case 'Idle':
      return 'Available';
    case 'LoggedOut':
      return 'Logged out';
  }
}

interface IProps {
  isOpen: boolean;
  onDismiss(): void;
  isSortedDescending: boolean;
  onIsSortedDescendingChange(value: boolean): void;
  sortOptionKey: string;
  onSortOptionKeyChange(value: string): void;
  filters: MemberFilterOptions;
  onFiltersChange(value: MemberFilterOptions): void;
}

const MemberSortAndFilterModal: FC<IProps> = ({ isOpen, onDismiss, isSortedDescending, onIsSortedDescendingChange, sortOptionKey, onSortOptionKeyChange, filters, onFiltersChange }) => {

  const reset = useCallback(
    () => {
      onIsSortedDescendingChange(false);
      onSortOptionKeyChange(sortDropdownOptions[0].key);
      onFiltersChange(defaultMemberFilters);
    },
    [onFiltersChange, onIsSortedDescendingChange, onSortOptionKeyChange],
  );

  return (
    <Modal
      isOpen={isOpen}
      onDismiss={onDismiss}
      isBlocking={false}
      styles={{ main: { width: 375 }, layer: { backgroundColor: 'rgba(255, 0, 0, 0.1)' } }}
    >
      <ModalHeader title="Sort and filter agents" />
      <Separator>Sort by</Separator>
      <Stack horizontal verticalAlign="center">
        <Stack.Item grow>
          <Dropdown
            selectedKey={sortOptionKey}
            options={sortDropdownOptions}
            onChange={(_, option) => onSortOptionKeyChange(option ? option.key as string : sortDropdownOptions[0].key)}
          />
        </Stack.Item>
        <ActionButton iconProps={{ iconName: isSortedDescending ? 'SortDown' : 'SortUp' }} onClick={() => onIsSortedDescendingChange(!isSortedDescending)}>
          {isSortedDescending ? 'Descending' : 'Ascending'}
        </ActionButton>
      </Stack>
      <br />
      <Separator>Filters</Separator>
      <InlineControl label="Show members without activity">
        <Toggle checked={!filters.hasCompletedTasks} onChange={(_, checked) => onFiltersChange({ ...filters, hasCompletedTasks: !checked })} />
      </InlineControl>
      <Label>Current state</Label>
      <Stack horizontal>
        {
          AgentStates.map(s =>
            <Checkbox key={s}
              label={getStateName(s)}
              checked={filters.states[s]}
              onChange={(_, checked) => onFiltersChange({ ...filters, states: { ...filters.states, [s]: !!checked } })}
              styles={{ root: { marginRight: '0.5rem' } }}
            />,
          )
        }
      </Stack>
      <br />
      <br />
      <Stack horizontal horizontalAlign="space-between">
        <DefaultButton iconProps={{ iconName: 'Refresh' }} onClick={reset}>Reset</DefaultButton>
        <PrimaryButton onClick={onDismiss}>Close</PrimaryButton>
      </Stack>
    </Modal>
  );
};

MemberSortAndFilterModal.displayName = nameof({ MemberSortAndFilterModal });
export default MemberSortAndFilterModal;